import { useAtomValue, useSetAtom } from 'jotai/react'
import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import tp from 'tp-js-sdk'

import { CHAIN_TYPE } from '@/hooks/services/useChainInfo'
import { TransferInfo, useTransferInfo } from '@/hooks/services/useTransferInfo'
import EthereumTools from '@/lib/EthereumTools'
import SolanaTools from '@/lib/SolanaTools'
import TronTools from '@/lib/TronTools'
import { addressAtom, fromChainAtom, isConnectedAtom, tradeToolsAtom } from '@/stores/trade'

interface WalletState {
  tpWallet?: {
    address: string
    blockchain: string
    blockchain_id: number
    name: string
    node: string
    ns: string
    walletType: string
  }
  transferInfos: TransferInfo[]
  setRefreshInterval: React.Dispatch<React.SetStateAction<number>>
}

const WalletContext = createContext<WalletState>({} as WalletState)

export const useWalletContext = () => useContext(WalletContext)

const WalletProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const fromChain = useAtomValue(fromChainAtom)
  const setTradeTools = useSetAtom(tradeToolsAtom)
  const setAddress = useSetAtom(addressAtom)
  const setIsConnected = useSetAtom(isConnectedAtom)

  const { transferInfos, setRefreshInterval } = useTransferInfo()

  const [tpWallet, setTpWallet] = useState<{
    address: string
    blockchain: string
    blockchain_id: number
    name: string
    node: string
    ns: string
    walletType: string
  }>()

  const initWallet = useCallback(async () => {
    if (!tp.isConnected() || !fromChain) return
    const { data } = await tp.getCurrentWallet()
    setTpWallet(data)
    setAddress(undefined)
    setIsConnected(false)
    console.log('>>>>>> data: ', data)

    switch (fromChain.type) {
      case CHAIN_TYPE.TRON:
        if (data.ns === 'tron') {
          setTradeTools(
            new TronTools(fromChain, (address, isConnected) => {
              setAddress(address)
              setIsConnected(isConnected)
            })
          )
        } else {
          toast.error(
            `当前钱包(${data.name})与${fromChain?.name}不匹配，点击右上角切换到${fromChain?.name}钱包进行交易。`
          )
        }
        break
      case CHAIN_TYPE.SOLANA:
        if (data.ns === 'solana') {
          setTradeTools(
            new SolanaTools(fromChain, (address, isConnected) => {
              setAddress(address)
              setIsConnected(isConnected)
            })
          )
        } else {
          toast.error(
            `当前钱包(${data.name})与${fromChain?.name}不匹配，点击右上角切换到${fromChain?.name}钱包进行交易。`
          )
        }
        break
      default:
        if (data.ns === 'ethereum')
          setTradeTools(
            new EthereumTools(fromChain, (address, isConnected) => {
              setAddress(address)
              setIsConnected(isConnected)
            })
          )
        else
          toast.error(
            `当前钱包(${data.name})与${fromChain?.name}不匹配，点击右上角切换到${fromChain?.name}钱包进行交易。`
          )
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromChain])

  useEffect(() => {
    initWallet()
  }, [initWallet])

  return (
    <WalletContext.Provider value={{ tpWallet, transferInfos, setRefreshInterval }}>{children}</WalletContext.Provider>
  )
}

export default WalletProvider
